import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Margen extra (px de scroll) que se suma a la altura del bloque siguiente
// para calcular la pausa de lectura antes de que empiece a cubrir al
// bloque pineado.
const READ_DELAY = 300;

let refreshTimeout;
const pinTriggers = [];
const pinEntries = [];
let pinnedBlocks = [];
let currentOrientation = window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape';
let pinToResetIndex = -1;

function refreshPinsAfterResize() {
    const nextOrientation = window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape';

    // Sólo recolocamos al inicio cuando se gira la pantalla. En un resize
    // normal se mantienen el scroll y el punto de lectura actuales.
    if (nextOrientation !== currentOrientation) {
        currentOrientation = nextOrientation;
        pinToResetIndex = pinTriggers.findIndex((pin) => pin.isActive);
    }

    clearTimeout(refreshTimeout);

    refreshTimeout = setTimeout(() => {
        requestAnimationFrame(() => {
            // ScrollSmoother transforma el contenido. Esperamos al final del
            // reflow para que ScrollTrigger mida el top real del bloque y
            // no conserve la posición intermedia del resize.
            if (window.gsapSmoother) {
                window.gsapSmoother.refresh();
            } else {
                ScrollTrigger.refresh();
            }

            // Un refresh actualiza medidas, pero no siempre reconstruye el
            // pin-spacer correctamente cuando el contenido está transformado
            // por ScrollSmoother. Se crean triggers nuevos con el layout final.
            rebuildPins();

            if (pinToResetIndex < 0) return;

            const pin = pinTriggers[pinToResetIndex];
            pinToResetIndex = -1;

            if (!pin) return;

            requestAnimationFrame(() => {
                if (window.gsapSmoother) {
                    window.gsapSmoother.scrollTo(pin.start, false);
                } else {
                    window.scrollTo({ top: pin.start, behavior: 'auto' });
                }
                ScrollTrigger.update();
            });
        });
    }, 500);
}

function rebuildPins() {
    pinTriggers.forEach((pin) => pin.kill(true));
    pinTriggers.length = 0;

    pinEntries.forEach(({ spacer, nextBlock }) => {
        spacer.remove();
        nextBlock.classList.remove('block--after-pin');
    });
    pinEntries.length = 0;

    createPins();
    ScrollTrigger.sort();
    ScrollTrigger.refresh();
}

function freezeParallax(parallaxContainers) {
    parallaxContainers.forEach(container => {
        const img = container.querySelector('img') || container.querySelector('video');
        if (img) {
            const currentYPercent = gsap.getProperty(img, "yPercent");
            container._frozenYPercent = currentYPercent;
            if (container._parallaxScrollTrigger) {
                container._parallaxScrollTrigger.disable();
            }
            gsap.set(img, { yPercent: currentYPercent });
        }
    });
}

function resumeParallax(parallaxContainers) {
    parallaxContainers.forEach(container => {
        if (container._parallaxScrollTrigger) {
            container._parallaxScrollTrigger.enable();
        }
    });
}

function createPins() {

    if (!pinnedBlocks.length) return;

    // Un solo pin por bloque, sin repinear el mismo elemento dos veces.
    // El "delay de lectura" se consigue reservando espacio real en el
    // documento con un spacer -pero, a diferencia del spacer original,
    // su altura NO es fija: se recalcula en cada refresh() de
    // ScrollTrigger (evento refreshInit, justo antes de medir
    // posiciones), así que sigue siendo correcta tras cualquier resize.
    pinnedBlocks.forEach((block, i) => {

        const nextBlock = block.nextElementSibling;
        if (!nextBlock) return;

        // El delay ocupa espacio real antes del siguiente bloque. Durante
        // ese recorrido el bloque actual sigue fijo; después el siguiente
        // atraviesa toda su altura por encima del pin.
        const spacer = document.createElement('div');
        spacer.className = 'block-pin-spacer';
        spacer.style.height = `${READ_DELAY}px`;
        block.insertAdjacentElement('afterend', spacer);
        nextBlock.classList.add('block--after-pin');

        // El spacer se inserta entre ambos bloques y deja de cumplirse el
        // selector CSS `[data-pin-block] + .block`. Marcamos el siguiente
        // bloque de forma explícita para que siempre se pinte por encima.
        const parallaxContainers = block.querySelectorAll('.animate__media');
        // Duración total del pin = pausa de lectura (altura real del
        // spacer) + altura del propio bloque (distancia que necesita
        // nextBlock, en flujo normal, para que su borde superior llegue
        // al top del viewport y lo cubra por completo). Al ser una
        // función, se re-evalúa en cada refresh -> resize-safe.
        const pin = ScrollTrigger.create({
            id: `block-pin-${i}`,
            trigger: block,
            start: 'top top',
            end: () => `+=${block.offsetHeight + READ_DELAY}`,
            pin: true,
            pinSpacing: false,
            invalidateOnRefresh: true,
            markers: false,
            onEnter: () => freezeParallax(parallaxContainers),
            onEnterBack: () => freezeParallax(parallaxContainers),
            onLeave: () => resumeParallax(parallaxContainers),
            onLeaveBack: () => resumeParallax(parallaxContainers),
        });

        pinTriggers.push(pin);
        pinEntries.push({ spacer, nextBlock });
    });

}

function blockPin() {
    pinnedBlocks = Array.from(document.querySelectorAll('[data-pin-block]'));
    if (!pinnedBlocks.length) return;

    window.addEventListener('resize', refreshPinsAfterResize);
    window.addEventListener('orientationchange', refreshPinsAfterResize);
    createPins();
}

export default blockPin;
